import { createHash, randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import path from "node:path";
import { runGitCommand } from "../utils/run-git-command.js";
import type { WorkspaceGitService } from "./workspace-git-service.js";

/**
 * Name-based project marker for non-git projects.
 *
 * The id lives in the *filename*, never the content: `.appostle/appostleproj-<id>`.
 * We resolve it by listing `.appostle/` and matching the name — we never read the
 * file's bytes. This makes the project key immune to Dropbox online-only stubs: a
 * placeholder still appears in the directory listing with its real name, only the
 * content is a stub, and we never touch the content.
 *
 * The marker is the no-git fallback for `repoKey`. Git projects always key off
 * their remote and never create or read this marker.
 */
const PROJECT_MARKER_PREFIX = "appostleproj-";

/**
 * Strict, anchored pattern. Dropbox sync-conflict siblings are named like
 * `appostleproj-<id> (conflicted copy 2026-...)` — they contain spaces/parens and
 * are therefore excluded, so a conflict copy can never be mistaken for the id.
 */
const PROJECT_MARKER_PATTERN = /^appostleproj-[a-z0-9]+$/;

/**
 * Resolve the `.appostle/` name-marker for a project, minting one if absent.
 *
 * - If one or more valid markers exist, return the lexicographically smallest
 *   (deterministic, so two machines that somehow both minted can't disagree).
 * - If none exists and the workspace directory is writable, mint
 *   `appostleproj-<uuid>` as an empty file and return it.
 * - If the workspace directory doesn't exist or can't be written, return null —
 *   the caller surfaces this as the "no key" (no_remote) state.
 */
/**
 * Read the existing `.appostle/appostleproj-<id>` marker WITHOUT minting one.
 * Used by the marker→git-remote transition: when a folder that was keyed by
 * its marker later gains a git remote, the marker becomes the legacy key the
 * auth-server re-keys from.
 */
export async function readProjectMarker(cwd: string): Promise<string | null> {
  const dotDir = path.join(cwd, ".appostle");
  let entries: string[] = [];
  try {
    entries = await fs.readdir(dotDir);
  } catch {
    return null;
  }
  const markers = entries.filter((name) => PROJECT_MARKER_PATTERN.test(name)).sort();
  return markers[0] ?? null;
}

/** Remove the `.appostle/appostleproj-<id>` marker file. ENOENT-safe. */
export async function deleteProjectMarker(cwd: string, marker: string): Promise<void> {
  if (!PROJECT_MARKER_PATTERN.test(marker)) return;
  try {
    await fs.unlink(path.join(cwd, ".appostle", marker));
  } catch {
    // already gone or unwritable — fine either way
  }
}

async function resolveProjectMarker(cwd: string): Promise<string | null> {
  const dotDir = path.join(cwd, ".appostle");

  let entries: string[] = [];
  try {
    entries = await fs.readdir(dotDir);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code !== "ENOENT" && code !== "EACCES" && code !== "EPERM") {
      throw err;
    }
    // .appostle/ doesn't exist yet (or isn't readable) — fall through.
  }

  const markers = entries.filter((name) => PROJECT_MARKER_PATTERN.test(name)).sort();
  const existing = markers[0];
  if (existing) {
    return existing;
  }

  // No marker yet. Only mint if the workspace directory actually exists — never
  // conjure `.appostle/` for a vanished/unwritable cwd.
  try {
    await fs.access(cwd);
  } catch {
    return null;
  }

  try {
    await fs.mkdir(dotDir, { recursive: true });
    const marker = `${PROJECT_MARKER_PREFIX}${randomUUID().replace(/-/g, "")}`;
    await fs.writeFile(path.join(dotDir, marker), "", "utf8");
    return marker;
  } catch {
    return null;
  }
}

/**
 * Normalize a raw git remote URL into a canonical repo key.
 *
 * Rules:
 * - Strip protocol (https://, ssh://, git@host: SCP form)
 * - Strip trailing .git
 * - Lowercase host only (path case is preserved)
 * - Drop trailing slash
 * - Return null for empty / null / whitespace input
 */
export function normalizeRemoteUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  let host: string;
  let remotePath: string;

  // SCP-like: git@github.com:foo/bar.git
  const scpMatch = trimmed.match(/^[^@/:]+@([^:]+):(.+)$/);
  if (scpMatch) {
    host = scpMatch[1] ?? "";
    remotePath = scpMatch[2] ?? "";
  } else if (trimmed.includes("://")) {
    // URL form: https://, ssh://, git://
    try {
      const parsed = new URL(trimmed);
      // Include port if non-standard (URL.host = hostname:port)
      host = parsed.host || "";
      remotePath = parsed.pathname ? parsed.pathname.replace(/^\/+/, "") : "";
    } catch {
      return null;
    }
  } else {
    return null;
  }

  if (!host || !remotePath) {
    return null;
  }

  // Strip trailing .git
  if (remotePath.endsWith(".git")) {
    remotePath = remotePath.slice(0, -4);
  }

  // Drop trailing slash on path
  remotePath = remotePath.replace(/\/+$/, "");

  if (!remotePath) {
    return null;
  }

  // Lowercase host only; preserve path case
  return `${host.toLowerCase()}/${remotePath}`;
}

/**
 * Deterministic last-resort key for directories that exist but where no marker
 * can be written (read-only mounts, `/`, network shares). Derived purely from
 * the normalized absolute path so every resolution — backfill, fan-out,
 * runtime patches — agrees on the same key.
 */
function derivePathFallbackKey(cwd: string): string {
  const normalized = path.resolve(cwd);
  const hash = createHash("sha256").update(normalized, "utf8").digest("hex").slice(0, 16);
  return `path:${hash}`;
}

/**
 * Resolve the canonical project key for a directory, uncached.
 *
 * Precedence is identical to `GitRemoteResolver.resolve`:
 *   1. Normalized git remote URL (e.g. `github.com/foo/bar`).
 *   2. The `.appostle/appostleproj-<id>` name-marker, minted on first use.
 *   3. A deterministic `path:<hash>` key derived from the normalized cwd, for
 *      directories that exist but can't hold a marker (read-only / unmarkable).
 *
 * This is THE project identity everywhere — todos, RemoteProject rows, tab
 * fan-outs. null means the directory itself is gone or unreadable.
 */
export async function resolveCanonicalProjectId(cwd: string): Promise<string | null> {
  const resolved = (await fetchRemoteKey(cwd)) ?? (await resolveProjectMarker(cwd));
  if (resolved) {
    return resolved;
  }

  try {
    await fs.access(cwd);
  } catch {
    return null;
  }
  return derivePathFallbackKey(cwd);
}

/**
 * Like `resolveCanonicalProjectId` but read-only: never mints a marker.
 * Use for lookups on folders we must not touch (e.g. archived workspaces
 * whose cloud rows are being cleaned up). The deterministic path fallback
 * is still returned — it has no side effects.
 */
export async function peekCanonicalProjectId(cwd: string): Promise<string | null> {
  const resolved = (await fetchRemoteKey(cwd)) ?? (await readProjectMarker(cwd));
  if (resolved) {
    return resolved;
  }
  try {
    await fs.access(cwd);
  } catch {
    return null;
  }
  return derivePathFallbackKey(cwd);
}

async function fetchRemoteKey(cwd: string): Promise<string | null> {
  // Try origin first
  try {
    const { stdout } = await runGitCommand(["remote", "get-url", "origin"], {
      cwd,
      acceptExitCodes: [0],
    });
    const normalized = normalizeRemoteUrl(stdout.trim());
    if (normalized) {
      return normalized;
    }
  } catch {
    // origin not found — fall through
  }

  // Fall back to first remote
  try {
    const { stdout: remoteList } = await runGitCommand(["remote"], {
      cwd,
      acceptExitCodes: [0],
    });
    const firstRemote = remoteList.trim().split("\n")[0]?.trim();
    if (!firstRemote) {
      return null;
    }

    const { stdout } = await runGitCommand(["remote", "get-url", firstRemote], {
      cwd,
      acceptExitCodes: [0],
    });
    return normalizeRemoteUrl(stdout.trim());
  } catch {
    return null;
  }
}

export class GitRemoteResolver {
  private readonly cache = new Map<string, string | null>();
  private readonly unsubscribes = new Map<string, () => void>();

  constructor(private readonly workspaceGitService: WorkspaceGitService) {}

  /**
   * Resolve the repoKey for the given workspace.
   *
   * Precedence:
   *   1. Git remote URL (normalized, e.g. `github.com/foo/bar`) — unchanged.
   *   2. No remote → the `.appostle/` name-marker (minted on first use), which
   *      gives non-git projects (plain folders, Dropbox-synced dirs) a stable,
   *      machine-independent key that travels with the folder.
   *
   * Result is cached. null means "no remote AND no marker could be established"
   * (e.g. the workspace dir is gone or read-only) — callers surface that as the
   * no_remote state.
   */
  async resolve(workspaceId: string, cwd: string): Promise<string | null> {
    if (this.cache.has(workspaceId)) {
      return this.cache.get(workspaceId) ?? null;
    }

    const result = await resolveCanonicalProjectId(cwd);
    this.cache.set(workspaceId, result);

    // Subscribe to git refresh events so we invalidate on remote changes
    if (!this.unsubscribes.has(workspaceId)) {
      const subscription = await this.workspaceGitService.subscribe({ cwd }, () => {
        this.invalidate(workspaceId);
      });
      this.unsubscribes.set(workspaceId, subscription.unsubscribe);
    }

    return result;
  }

  invalidate(workspaceId: string): void {
    this.cache.delete(workspaceId);
  }

  invalidateAll(): void {
    this.cache.clear();
  }
}
